import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  useCreateSectionMutation,
  useCreatePageMutation,
  useCreatePageModuleMutation,
} from '../../editCourse/editCourseApi';
import { useUpdateCourseMutation } from '../../course/courseApi';
import { useCreateQuizMutation } from '../../quizes/quizesApi';
import {
  endGuide,
  setIsPopulating,
  setPopulationProgress,
  selectGuideState,
} from '../courseGuideState';
import { populateCourseFromGuide } from '../utils/guidePopulator';
import { notifySuccess, notifyError } from '../../../services/notifications';

export const useGuidePopulation = (courseId) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const guideState = useSelector(selectGuideState);

  const [updateCourseTrigger] = useUpdateCourseMutation();
  const [createSectionTrigger] = useCreateSectionMutation();
  const [createPageTrigger] = useCreatePageMutation();
  const [createPageModuleTrigger] = useCreatePageModuleMutation();
  const [createQuizTrigger] = useCreateQuizMutation();

  const populate = useCallback(async () => {
    dispatch(setIsPopulating(true));
    dispatch(setPopulationProgress(0));

    try {
      const result = await populateCourseFromGuide({
        courseId,
        guideState,
        t,
        mutations: {
          updateCourse: updateCourseTrigger,
          createSection: createSectionTrigger,
          createPage: createPageTrigger,
          createPageModule: createPageModuleTrigger,
          createQuiz: createQuizTrigger,
        },
        onProgress: (progress) => {
          dispatch(setPopulationProgress(progress));
        },
      });

      notifySuccess(t('courseGuide.populationSuccess'));
      dispatch(endGuide());

      // Navigate to the first page of the newly built course
      if (result.courseId && result.firstPageId) {
        navigate(`/edit/course/${result.courseId}/${result.firstPageId}`, { replace: true });
      } else if (result.courseId) {
        navigate(`/edit/course/${result.courseId}`, { replace: true });
      }
    } catch (error) {
      console.error('Guide population failed:', error);
      notifyError(t('courseGuide.populationFailed'));
      dispatch(setIsPopulating(false));
    }
  }, [
    courseId,
    guideState,
    t,
    updateCourseTrigger,
    createSectionTrigger,
    createPageTrigger,
    createPageModuleTrigger,
    createQuizTrigger,
    dispatch,
    navigate,
  ]);

  return { populate };
};
